import { ConfigDatabase } from '../../engine/database';
import { galaxiesData, type GalaxyConfig } from '../../generated/galaxies';
import { sectorsData, type SectorConfig } from '../../generated/sectors';

export type GalaxyId = string;
export type SectorId = string;

/**
 * Интерфейс для сектора с дополнительной информацией
 */
export interface SectorInfo extends SectorConfig {
  galaxyInfo: GalaxyConfig;
  sectorId: string;
  isUnlocked: boolean;
  difficulty: number;
}

/**
 * Интерфейс для галактики с дополнительной информацией
 */
export interface GalaxyInfo extends GalaxyConfig {
  sectors: SectorInfo[];
  totalSectors: number;
  unlockedSectors: number;
}

/**
 * База данных для игрового мира
 * Объединяет галактики и секторы
 */
export class GameWorldDB {
  private galaxiesDB: ConfigDatabase<GalaxyConfig, string>;
  private sectorsDB: ConfigDatabase<SectorConfig, string>;

  constructor() {
    this.galaxiesDB = new ConfigDatabase({
      base: galaxiesData,
      overrides: {},
      getId: config => config.galaxy_id,
      validate: (config, id) => {
        if (config.base_hp_mult <= 0) {
          throw new Error(`Galaxy ${id}: base_hp_mult must be > 0`);
        }
        if (config.base_armor_step < 0) {
          throw new Error(`Galaxy ${id}: base_armor_step must be >= 0`);
        }
      },
    });

    // Создаем Record из массива sectorsData
    const sectorsRecord: Record<string, SectorConfig> = {};
    sectorsData.forEach(sector => {
      const id = `${sector.Galaxy}-${sector.idx}`;
      sectorsRecord[id] = sector;
    });

    this.sectorsDB = new ConfigDatabase({
      base: sectorsRecord,
      overrides: {},
      getId: config => `${config.Galaxy}-${config.idx}` as string,
      validate: (config, id) => {
        if (config.hp_mult <= 0) {
          throw new Error(`Sector ${id}: hp_mult must be > 0`);
        }
        if (config.density < 0) {
          throw new Error(`Sector ${id}: density must be >= 0`);
        }
      },
    });
  }

  /**
   * Инициализация всех внутренних баз данных
   */
  init(): void {
    this.galaxiesDB.init();
    this.sectorsDB.init();
  }

  // ===== GALAXIES =====

  /**
   * Получить информацию о галактике
   */
  getGalaxyInfo(galaxyId: GalaxyId): GalaxyInfo | undefined {
    const galaxy = this.galaxiesDB.get(galaxyId);
    if (!galaxy) return undefined;

    const sectors = this.getSectorsByGalaxy(galaxyId);
    const unlockedSectors = sectors.filter(s => s.isUnlocked).length;

    return {
      ...galaxy,
      sectors,
      totalSectors: sectors.length,
      unlockedSectors,
    };
  }

  /**
   * Получить все галактики
   */
  getAllGalaxies(): GalaxyInfo[] {
    return this.galaxiesDB.all().map(galaxy => this.getGalaxyInfo(galaxy.galaxy_id)!);
  }

  /**
   * Получить галактики по порядку
   */
  getGalaxiesInOrder(): GalaxyInfo[] {
    return this.getAllGalaxies().sort((a, b) => {
      // Сортируем по ID галактики (G1, G2, G3...)
      const aNum = parseInt(a.galaxy_id.replace('G', ''));
      const bNum = parseInt(b.galaxy_id.replace('G', ''));
      return aNum - bNum;
    });
  }

  // ===== SECTORS =====

  /**
   * Получить информацию о секторе
   */
  getSectorInfo(galaxyId: GalaxyId, sectorIdx: number): SectorInfo | undefined {
    const sectorId = `${galaxyId}-${sectorIdx}`;
    const sector = this.sectorsDB.get(sectorId);
    if (!sector) return undefined;

    const galaxyInfo = this.galaxiesDB.get(galaxyId);
    if (!galaxyInfo) return undefined;

    // TODO: Интегрировать с системой прогресса игрока
    const isUnlocked = sectorIdx === 1; // Пока только первый сектор разблокирован

    // Вычисляем сложность на основе HP мультипликатора
    const difficulty = sector.hp_mult * galaxyInfo.base_hp_mult;

    return {
      ...sector,
      galaxyInfo,
      sectorId,
      isUnlocked,
      difficulty,
    };
  }

  /**
   * Получить секторы галактики
   */
  getSectorsByGalaxy(galaxyId: GalaxyId): SectorInfo[] {
    return this.sectorsDB
      .all()
      .filter(sector => sector.Galaxy === galaxyId)
      .map(sector => this.getSectorInfo(galaxyId, sector.idx)!)
      .sort((a, b) => a.idx - b.idx);
  }

  /**
   * Получить все секторы
   */
  getAllSectors(): SectorInfo[] {
    return this.sectorsDB.all().map(sector => this.getSectorInfo(sector.Galaxy, sector.idx)!);
  }

  /**
   * Получить секторы по миру (галактике)
   */
  getSectorsByWorld(world: number): SectorInfo[] {
    const galaxyId = `G${world}`;
    return this.getSectorsByGalaxy(galaxyId);
  }

  /**
   * Получить разблокированные секторы
   */
  getUnlockedSectors(): SectorInfo[] {
    return this.getAllSectors().filter(sector => sector.isUnlocked);
  }

  /**
   * Получить следующий сектор для разблокировки
   */
  getNextSectorToUnlock(galaxyId: GalaxyId): SectorInfo | undefined {
    const sectors = this.getSectorsByGalaxy(galaxyId);
    return sectors.find(sector => !sector.isUnlocked);
  }

  // ===== UTILITY METHODS =====

  /**
   * Получить сектор по ID
   */
  getSectorById(sectorId: string): SectorInfo | undefined {
    const [galaxyId, idxStr] = sectorId.split('-');
    const idx = parseInt(idxStr);
    return this.getSectorInfo(galaxyId, idx);
  }

  /**
   * Получить все ID галактик
   */
  getAllGalaxyIds(): GalaxyId[] {
    return this.galaxiesDB.ids() as GalaxyId[];
  }

  /**
   * Получить все ID секторов
   */
  getAllSectorIds(): SectorId[] {
    return this.sectorsDB.ids() as SectorId[];
  }

  /**
   * Проверить существование галактики
   */
  hasGalaxy(galaxyId: GalaxyId): boolean {
    return this.galaxiesDB.has(galaxyId);
  }

  /**
   * Проверить существование сектора
   */
  hasSector(galaxyId: GalaxyId, sectorIdx: number): boolean {
    const sectorId = `${galaxyId}-${sectorIdx}`;
    return this.sectorsDB.has(sectorId);
  }

  /**
   * Получить общую информацию о прогрессе
   */
  getProgressInfo(): {
    totalGalaxies: number;
    totalSectors: number;
    unlockedSectors: number;
    progressPercentage: number;
  } {
    const galaxies = this.getAllGalaxies();
    const sectors = this.getAllSectors();
    const unlockedSectors = this.getUnlockedSectors();

    return {
      totalGalaxies: galaxies.length,
      totalSectors: sectors.length,
      unlockedSectors: unlockedSectors.length,
      progressPercentage: sectors.length > 0 ? (unlockedSectors.length / sectors.length) * 100 : 0,
    };
  }

  /**
   * Найти секторы по сложности
   */
  getSectorsByDifficulty(minDifficulty: number, maxDifficulty: number): SectorInfo[] {
    return this.getAllSectors().filter(
      sector => sector.difficulty >= minDifficulty && sector.difficulty <= maxDifficulty
    );
  }

  /**
   * Получить самый сложный сектор
   */
  getHardestSector(): SectorInfo | undefined {
    const sectors = this.getAllSectors();
    return sectors.reduce((hardest, current) =>
      current.difficulty > hardest.difficulty ? current : hardest
    );
  }

  /**
   * Получить самый легкий сектор
   */
  getEasiestSector(): SectorInfo | undefined {
    const sectors = this.getAllSectors();
    return sectors.reduce((easiest, current) =>
      current.difficulty < easiest.difficulty ? current : easiest
    );
  }
}

// Ленивый синглтон для удобства
let _gameWorldInstance: GameWorldDB | null = null;
export function getGameWorld(): GameWorldDB {
  if (!_gameWorldInstance) {
    _gameWorldInstance = new GameWorldDB();
  }
  return _gameWorldInstance;
}

// Синглтон для удобства
export const GameWorld = new Proxy({} as GameWorldDB, {
  get(_target, prop) {
    return getGameWorld()[prop as keyof GameWorldDB];
  },
});

// Реэкспортируем типы для удобства
export type { GalaxyConfig, SectorConfig };
